import styled from "styled-components";
// Path assumes this file lives at
// src/components/sections/WelcomeScreen/DietPlan/DietPlanScreen.styles.js
import { DIET_C } from "../../../common/colors";

// ═══════════════════════════════════════════════════════════════════════════════
// DIET PLAN SCREEN
// Full-screen, not wizard-scoped — falls back to literal values where the
// AssessmentStepShell-scoped --c-*/--sp-* custom properties wouldn't be
// in scope, same approach as LoadingScreen.styles.js.
// ═══════════════════════════════════════════════════════════════════════════════

export const Screen = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: var(--sp-6, 32px) var(--sp-4, 16px) 96px;
`;
Screen.displayName = "Screen";

export const Header = styled.div`
  margin-bottom: var(--sp-6, 32px);
`;
Header.displayName = "Header";

export const Title = styled.h1`
  margin: 0 0 4px;
  color: var(--c-text, #1a1a1a);
  font-size: 1.4rem;
  font-weight: 800;
`;
Title.displayName = "Title";

export const Subtitle = styled.p`
  margin: 0;
  color: var(--c-text-muted, #666);
  font-size: 0.9rem;
`;
Subtitle.displayName = "Subtitle";

// ─── Risk notice ─────────────────────────────────────────────────────────────
export const RiskNotice = styled.div`
  padding: var(--sp-4, 16px);
  border-radius: var(--r-sm, 10px);
  background: ${DIET_C.accent}12;
  border: 1.5px solid ${DIET_C.accent};
  margin-bottom: var(--sp-6, 32px);
`;
RiskNotice.displayName = "RiskNotice";

export const RiskNoticeTitle = styled.p`
  margin: 0 0 6px;
  color: ${DIET_C.accent};
  font-weight: 800;
  font-size: 0.95rem;
`;
RiskNoticeTitle.displayName = "RiskNoticeTitle";

export const RiskNoticeText = styled.p`
  margin: 0 0 var(--sp-3, 12px);
  color: var(--c-text, #1a1a1a);
  font-size: 0.88rem;
  line-height: 1.5;
`;
RiskNoticeText.displayName = "RiskNoticeText";

export const AcknowledgeButton = styled.button`
  padding: 9px 20px;
  border-radius: 999px;
  background: ${DIET_C.accent};
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  border: none;

  &:focus-visible {
    outline: 3px solid ${DIET_C.accent};
    outline-offset: 2px;
  }
`;
AcknowledgeButton.displayName = "AcknowledgeButton";

// ─── Health summary dashboard ────────────────────────────────────────────────
export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--sp-3, 12px);
  margin-bottom: var(--sp-6, 32px);

  @media (max-width: 40em) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
SummaryGrid.displayName = "SummaryGrid";

export const SummaryCard = styled.div`
  padding: var(--sp-3, 12px);
  border-radius: var(--r-sm, 10px);
  background: var(--c-surface, #fafafa);
  border: 1px solid ${DIET_C.secondary}33;
  text-align: center;
`;
SummaryCard.displayName = "SummaryCard";

export const SummaryValue = styled.p`
  margin: 0;
  color: ${DIET_C.primary};
  font-size: 1.15rem;
  font-weight: 800;
`;
SummaryValue.displayName = "SummaryValue";

export const SummaryLabel = styled.p`
  margin: 2px 0 0;
  color: var(--c-text-muted, #666);
  font-size: 0.75rem;
  font-weight: 600;
`;
SummaryLabel.displayName = "SummaryLabel";

// ─── Meal cards ──────────────────────────────────────────────────────────────
export const MealList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4, 16px);
  margin-bottom: var(--sp-6, 32px);
`;
MealList.displayName = "MealList";

export const MealCard = styled.div`
  border: 1px solid ${DIET_C.secondary}33;
  border-radius: var(--r-sm, 10px);
  background: var(--c-surface, #fafafa);
  padding: var(--sp-4, 16px);
`;
MealCard.displayName = "MealCard";

export const MealHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--sp-3, 12px);
`;
MealHeader.displayName = "MealHeader";

export const MealSlotLabel = styled.p`
  margin: 0;
  color: var(--c-text-muted, #666);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;
MealSlotLabel.displayName = "MealSlotLabel";

export const MealName = styled.h3`
  margin: 2px 0 4px;
  color: var(--c-text, #1a1a1a);
  font-size: 1.02rem;
  font-weight: 700;
`;
MealName.displayName = "MealName";

export const MealMacros = styled.p`
  margin: 0;
  color: var(--c-text-muted, #666);
  font-size: 0.82rem;
`;
MealMacros.displayName = "MealMacros";

export const MealActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
`;
MealActions.displayName = "MealActions";

export const IconButton = styled.button`
  padding: 6px 14px;
  border-radius: 999px;
  background: transparent;
  color: ${DIET_C.primary};
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  border: 1.5px solid ${DIET_C.primary};
  white-space: nowrap;
  transition: background var(--t-fast, 0.2s);

  &:hover:not(:disabled) {
    background: ${DIET_C.primary}14;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid var(--c-accent, ${DIET_C.accent});
    outline-offset: 2px;
  }
`;
IconButton.displayName = "IconButton";

export const RecipePanel = styled.div`
  margin-top: var(--sp-3, 12px);
  padding-top: var(--sp-3, 12px);
  border-top: 1px dashed ${DIET_C.secondary}44;
`;
RecipePanel.displayName = "RecipePanel";

export const IngredientRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2, 8px);
  padding: 6px 0;
  font-size: 0.85rem;
  color: var(--c-text, #1a1a1a);
`;
IngredientRow.displayName = "IngredientRow";

export const SubstituteRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 4px 0 10px;
`;
SubstituteRow.displayName = "SubstituteRow";

export const SubstituteChip = styled.button`
  padding: 4px 12px;
  border-radius: 999px;
  background: var(--c-surface, #fff);
  color: var(--c-text, #1a1a1a);
  font-size: 0.78rem;
  cursor: pointer;
  border: 1.5px solid ${DIET_C.secondary}66;

  &:hover {
    border-color: ${DIET_C.primary};
  }
`;
SubstituteChip.displayName = "SubstituteChip";

export const StepsList = styled.ol`
  margin: var(--sp-2, 8px) 0 0;
  padding-left: 1.1em;
  color: var(--c-text-muted, #666);
  font-size: 0.85rem;
  line-height: 1.6;
`;
StepsList.displayName = "StepsList";

export const InlineNotice = styled.p`
  margin: 6px 0 0;
  color: var(--c-text-muted, #666);
  font-size: 0.8rem;
  font-style: italic;
`;
InlineNotice.displayName = "InlineNotice";

// ─── Daily tips + disclaimer ─────────────────────────────────────────────────
export const TipsList = styled.ul`
  margin: 0 0 var(--sp-6, 32px);
  padding-left: 1.2em;
  color: var(--c-text, #1a1a1a);
  font-size: 0.88rem;
  line-height: 1.7;
`;
TipsList.displayName = "TipsList";

export const Disclaimer = styled.p`
  color: var(--c-text-muted, #666);
  font-size: 0.78rem;
  line-height: 1.5;
  margin: 0 0 var(--sp-6, 32px);
`;
Disclaimer.displayName = "Disclaimer";

// ─── Bottom actions ──────────────────────────────────────────────────────────
export const ActionBar = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  gap: var(--sp-3, 12px);
  padding: var(--sp-3, 12px) 0;
  background: var(--c-bg, #fff);
  border-top: 1px solid ${DIET_C.secondary}33;

  @media (max-width: 40em) {
    flex-direction: column;
  }
`;
ActionBar.displayName = "ActionBar";

export const PrimaryActionButton = styled.button`
  flex: 1;
  height: 46px;
  border-radius: 999px;
  background: ${DIET_C.primary};
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: opacity var(--t-fast, 0.2s);

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
PrimaryActionButton.displayName = "PrimaryActionButton";

export const SecondaryActionButton = styled(PrimaryActionButton)`
  background: transparent;
  color: ${DIET_C.primary};
  border: 1.5px solid ${DIET_C.primary};
`;
SecondaryActionButton.displayName = "SecondaryActionButton";
